/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/alt-text */
import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import IconOkay from 'assets/images/icons/icn-ok.png'
import IconWarning from 'assets/images/icons/icn-warning.png'
import IconCritical from 'assets/images/icons/icn-critical.png'
import IconFlag from 'assets/images/icons/icn-flag.svg'

import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const URLS = {
  CAMPUS_DOMAINS: 'https://campus.barracuda.com/doc/96022982/',
  CAMPUS_VERIFICATION: 'https://campus.barracuda.com/doc/96022984/'
}

function DomainManagerI18n({
  classes,
  productName,
  hasCpl
}: {
  classes: ReturnType<typeof styles>
  productName?: string
  hasCpl?: boolean
}) {
  const formatMessage = useFormatMessage('ess.help_domain_manager')
  return (
    <div className={classes.content} data-testid="content">
      <p>{formatMessage('intro', { productName })}</p>
      <a href={URLS.CAMPUS_DOMAINS} target="_blank" rel="noreferrer">
        {' '}
        {formatMessage('campus_link')}
      </a>
      <br />
      <br />
      {formatMessage('sorting')}
      <br />
      <br />
      {formatMessage('status.title')}
      <ul>
        <li>
          <img src={IconOkay} />
          {formatMessage('status.verified', { productName })}
        </li>
        <li>
          <img src={IconWarning} />
          {formatMessage('status.warning', { productName })}
        </li>
        <li>
          <img src={IconCritical} />
          {formatMessage('status.critical')}
        </li>
      </ul>
      {formatMessage('status_resolution_prefix')}{' '}
      <a href={URLS.CAMPUS_VERIFICATION} target="_blank" rel="noreferrer">
        {' '}
        {formatMessage('status_resolution_link')}
      </a>{' '}
      {formatMessage('status_resolution_suffix')}
      <img src={IconOkay} />.
      <h3>
        <a>{formatMessage('domain_options.title')}</a>
      </h3>
      {formatMessage('domain_options.intro')}
      <ul>
        <li>
          <b>{formatMessage('domain_options.aliases.title')}</b>
          <br />
          {formatMessage('domain_options.aliases.description')}
        </li>
        <li>
          <b>{formatMessage('domain_options.recipients.title')}</b>
          <br />
          {formatMessage('domain_options.recipients.description')}
        </li>
        <li>
          <b>{formatMessage('domain_options.verify_ownership.title')}</b>
          <br />
          {formatMessage('domain_options.verify_ownership.description')}
        </li>
        <li>
          <b>{formatMessage('domain_options.edit_domain_settings.title')}</b>
          <br />
          {formatMessage('domain_options.edit_domain_settings.description')}
        </li>
        {!hasCpl && (
          <li>
            <b>{formatMessage('domain_options.manage.title')}</b>
            <br />
            {formatMessage('domain_options.manage.description')}
          </li>
        )}
        <li>
          <b>{formatMessage('domain_options.remove.title')}</b>
          <br />
          {formatMessage('domain_options.remove.description', { productName })}
        </li>
        <li>
          <b>{formatMessage('domain_options.reset_policies.title')}</b>
          <br />
          {formatMessage('domain_options.reset_policies.description_prefix')}
          <img src={IconFlag} />
          {formatMessage('domain_options.reset_policies.description_suffix')}
        </li>
      </ul>
      <h3>
        <a>{formatMessage('add_domain.title')}</a>
      </h3>
      <ol>
        <li>{formatMessage('add_domain.step1')}</li>
        <li>{formatMessage('add_domain.step2')}</li>
        <li>{formatMessage('add_domain.step3')}</li>
        <li>{formatMessage('add_domain.step4', { productName })}</li>
      </ol>
      {!hasCpl && (
        <>
          <h3>
            <a>{formatMessage('relay_outbound.title')}</a>
          </h3>
          {formatMessage('relay_outbound.description')}
        </>
      )}
    </div>
  )
}

function DomainManager(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  if (isSmartlingEnabled) {
    return <DomainManagerI18n classes={classes} productName={props.productName} hasCpl={props.hasCpl} />
  }

  return (
    <div className={classes.content} data-testid="content">
      <p>
        The <b>Domains</b> page displays all domains added to {props.productName} for email processing. Use this page to
        manage existing domains and add new domains. For more information on the <b>Domains</b> page, see{' '}
      </p>
      <a href="https://campus.barracuda.com/doc/96022982/" target="_blank" rel="noreferrer">
        {' '}
        Barracuda Campus.
      </a>
      <br />
      <br />
      To sort by <b>Domain Name</b>, click on the column heading. Click on a domain name to toggle additional details
      and actions.
      <br />
      <br />
      The <b>Domain Name</b> column displays the Domain status as follows:
      <ul>
        <li>
          <img src={IconOkay} /> indicates the Domain is verified and all MX records for the domain are pointing to{' '}
          {props.productName}.
        </li>
        <li>
          <img src={IconWarning} /> indicates the Domain is verified but the MX records are not all pointing to{' '}
          {props.productName}, and therefore MX is not configured.
        </li>
        <li>
          <img src={IconCritical} /> indicates the Domain is new and not yet verified. Click <b>Verify Ownership</b> to
          complete the verification process.
        </li>
      </ul>
      See{' '}
      <a href="https://campus.barracuda.com/doc/96022984/" target="_blank" rel="noreferrer">
        {' '}
        Barracuda Campus
      </a>{' '}
      for information on resolving states other than verified <img src={IconOkay} />.
      <h3>
        <a>Domain Options</a>
      </h3>
      You can take the following actions on a domain:
      <ul>
        <li>
          <b>Aliases</b>
          <br />
          Click the link in the <b>Aliases</b> column to go to the <b>Domains &gt; Domain Settings</b> page where you
          can modify the domain alias.
        </li>
        <li>
          <b>Recipients</b>
          <br />
          To view the list of unique recipients seen in the last 30 days for a domain, click the recipient value in the{' '}
          <b>Recipients</b>
          column for that domain. The <b>Recipients List</b> page displays.
        </li>
        <li>
          <b>Verify Ownership</b>
          <br />
          Click <b>Verify Ownership</b> in the <b>Domain Name</b> column to go to the <b>Domain Verification</b> page to
          complete the verification process.
        </li>
        <li>
          <b>Edit Domain Settings</b>
          <br />
          If you have not completed the verification process, click <b>Edit</b> in the <b>Settings</b> column to go to
          the <b>Domains &gt; Domain Settings</b> page.
        </li>
        {!props.hasCpl && (
          <li>
            <b>Manage</b>
            <br />
            Click <b>Manage</b> in the <b>Domain Options</b> column to view the dashboard and configure inbound and
            outbound policies for the domain.
          </li>
        )}
        <li>
          <b>Remove</b>
          <br />
          Click <b>Remove</b> in the <b>Domain Options</b> column to remove the selected domain; once removed,{' '}
          {props.productName} no longer processes mail for that domain.
        </li>
        <li>
          <b>Reset to account policies</b>
          <br />
          If you have set custom policies on a domain, the <b>Domain Specific Policies</b> (<img src={IconFlag} />) icon
          displays. Click the domain name to toggle details on. <b>Domain Specific Policies</b>
          displays in the <b>Domain Options</b> field. To revert the domain to the global settings, click{' '}
          <b>Reset to account policies</b>.
        </li>
      </ul>
      <h3>
        <a>Add a New Domain</a>
      </h3>
      <ol>
        <li>
          Click <b>Add Domain</b>.
        </li>
        <li>
          Enter the <b>Domain Name</b> and <b>Mail Server</b> hostname (FQDN) or IP address.
        </li>
        <li>
          Click <b>Add</b>. Once the domain is added, the <b>Domains &gt; Domain Settings</b> page displays.
        </li>
        <li>
          Each of the domains where you want to filter email must be verified by {props.productName} for proof of
          ownership; {props.productName} does not process email for a domain until the verification process is complete.
          Use the <b>Domains &gt; Domain Settings</b> page to complete the verification process, configure mail servers,
          SMTP over TLS, encryption, authentication, and other domain-specific settings.
        </li>
      </ol>
      {!props.hasCpl && (
        <>
          <h3>
            <a>Relay Outbound Mail</a>
          </h3>
          In your mail server or Smart host, specify the hostname value from the <b>Outbound Hostname</b> field for each
          domain from which you want to relay outbound mail.
        </>
      )}
    </div>
  )
}

export default DomainManager
/* eslint-enable jsx-a11y/anchor-is-valid, jsx-a11y/alt-text */
